import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { BrandProvider, useBrand } from "./lib/brand";
import { I18nProvider } from "./lib/i18n";
import { Toaster } from "sonner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RegisterSuccessPage from "./pages/RegisterSuccessPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AppLayout from "./components/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import CollectionsPage from "./pages/CollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import GarmentDevelopmentPage from "./pages/GarmentDevelopmentPage";
import GarmentImportPage from "./pages/GarmentImportPage";
import SamplingDigestPage from "./pages/SamplingDigestPage";
import ProductionsPage from "./pages/ProductionsPage";
import GarmentsPage from "./pages/GarmentsPage";
import MaterialsPage from "./pages/MaterialsPage";
import SuppliersPage from "./pages/SuppliersPage";
import OrdersPage from "./pages/OrdersPage";
import TasksPage from "./pages/TasksPage";
import InventoryPage from "./pages/InventoryPage";
import ClientsOrdersPage, { RedirectToClientsTab, RedirectToOrdersTab } from "./pages/ClientsOrdersPage";
import CollectionsGarmentsPage, { RedirectToCollectionsTab, RedirectToGarmentsTab } from "./pages/CollectionsGarmentsPage";
import MaterialsInventoryPage, { RedirectToMaterialsTab, RedirectToInventoryTab } from "./pages/MaterialsInventoryPage";
import AiPage from "./pages/AiPage";
import SelectBrandPage from "./pages/SelectBrandPage";
import OnboardingPage from "./pages/OnboardingPage";
import BrandSettingsPage from "./pages/BrandSettingsPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import TeamPage from "./pages/TeamPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import FolderPage from "./pages/FolderPage";
import ChatPage from "./pages/ChatPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import EmailProviderPage from "./pages/EmailProviderPage";
import AuthCallback from "./pages/AuthCallback";
import MmsSimulatorPage from "./pages/MmsSimulatorPage";
import MmsSimulatorManualPage from "./pages/MmsSimulatorManualPage";
import MmsPricingAdminPage from "./pages/MmsPricingAdminPage";
import MmsQuoteDashboardPage from "./pages/MmsQuoteDashboardPage";
import "./index.css";

function Loading() {
  return <div className="min-h-screen bg-[#FAFAFA] text-[#737373] flex items-center justify-center text-xs uppercase tracking-widest">Loading…</div>;
}

function Protected({ children, needBrand = true }) {
  const { user, ready } = useAuth();
  const { activeBrand, ready: bready } = useBrand();
  if (!ready || (needBrand && !bready)) return <Loading/>;
  if (!user) return <Navigate to="/login" replace />;
  if (needBrand && !activeBrand) return <Navigate to="/select-brand" replace />;
  return children;
}

function Screens() {
  // If Google OAuth returned with #session_id in the URL, force through AuthCallback
  // regardless of the current path so we exchange the session before any Protected route
  // decides to redirect us to /login.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback/>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage/>}/>
      <Route path="/register" element={<RegisterPage/>}/>
      <Route path="/register-success" element={<RegisterSuccessPage/>}/>
      <Route path="/verify-email" element={<VerifyEmailPage/>}/>
      <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
      <Route path="/reset-password" element={<ResetPasswordPage/>}/>
      <Route path="/auth/callback" element={<AuthCallback/>}/>
      <Route path="/select-brand" element={<Protected needBrand={false}><SelectBrandPage/></Protected>}/>
      <Route path="/onboarding" element={<Protected needBrand={false}><OnboardingPage/></Protected>}/>
      <Route path="/admin" element={<Protected needBrand={false}><SuperAdminPage/></Protected>}/>
      <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
      <Route path="/dashboard" element={<Protected><AppLayout><DashboardPage/></AppLayout></Protected>}/>
      {/* Merged sections (Iter 42) */}
      <Route path="/clients-orders" element={<Protected><AppLayout><ClientsOrdersPage/></AppLayout></Protected>}/>
      <Route path="/collections-garments" element={<Protected><AppLayout><CollectionsGarmentsPage/></AppLayout></Protected>}/>
      <Route path="/garments/import" element={<Protected><AppLayout><GarmentImportPage/></AppLayout></Protected>}/>
      <Route path="/materials-inventory" element={<Protected><AppLayout><MaterialsInventoryPage/></AppLayout></Protected>}/>
      {/* Legacy paths → redirect to the matching tab */}
      <Route path="/clients" element={<Protected><RedirectToClientsTab/></Protected>}/>
      <Route path="/orders" element={<Protected><RedirectToOrdersTab/></Protected>}/>
      <Route path="/collections" element={<Protected><RedirectToCollectionsTab/></Protected>}/>
      <Route path="/collections/:id" element={<Protected><AppLayout><CollectionDetailPage/></AppLayout></Protected>}/>
      <Route path="/garments/:id/development" element={<Protected><AppLayout><GarmentDevelopmentPage/></AppLayout></Protected>}/>
      <Route path="/sampling-digest/:id" element={<Protected><AppLayout><SamplingDigestPage/></AppLayout></Protected>}/>
      <Route path="/productions" element={<Protected><AppLayout><ProductionsPage/></AppLayout></Protected>}/>
      <Route path="/garments" element={<Protected><RedirectToGarmentsTab/></Protected>}/>
      <Route path="/materials" element={<Protected><RedirectToMaterialsTab/></Protected>}/>
      <Route path="/inventory" element={<Protected><RedirectToInventoryTab/></Protected>}/>
      <Route path="/suppliers" element={<Protected><AppLayout><SuppliersPage/></AppLayout></Protected>}/>
      <Route path="/tasks" element={<Protected><AppLayout><TasksPage/></AppLayout></Protected>}/>
      <Route path="/ai" element={<Protected><AppLayout><AiPage/></AppLayout></Protected>}/>
      <Route path="/mms-simulator" element={<Protected><AppLayout><MmsSimulatorPage/></AppLayout></Protected>}/>
      <Route path="/mms-simulator/:sessionId" element={<Protected><AppLayout><MmsSimulatorPage/></AppLayout></Protected>}/>
      <Route path="/mms-simulator/manual" element={<Protected><AppLayout><MmsSimulatorManualPage/></AppLayout></Protected>}/>
      <Route path="/mms-chat" element={<Navigate to="/mms-simulator" replace/>}/>
      <Route path="/mms-chat/:sessionId" element={<Navigate to="/mms-simulator" replace/>}/>
      <Route path="/admin/mms-pricing" element={<Protected><AppLayout><MmsPricingAdminPage/></AppLayout></Protected>}/>
      <Route path="/admin/mms-simulations" element={<Protected><AppLayout><MmsQuoteDashboardPage/></AppLayout></Protected>}/>
      <Route path="/settings/brand" element={<Protected><AppLayout><BrandSettingsPage/></AppLayout></Protected>}/>
      <Route path="/team" element={<Protected><AppLayout><TeamPage/></AppLayout></Protected>}/>
      <Route path="/folder" element={<Protected><AppLayout><FolderPage/></AppLayout></Protected>}/>
      <Route path="/chat" element={<Protected><AppLayout><ChatPage/></AppLayout></Protected>}/>
      <Route path="/settings/notifications" element={<Protected><AppLayout><NotificationSettingsPage/></AppLayout></Protected>}/>
      <Route path="/settings/email-provider" element={<Protected needBrand={false}><EmailProviderPage/></Protected>}/>
      <Route path="/audit-logs" element={<Protected><AppLayout><AuditLogsPage/></AppLayout></Protected>}/>
      <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
        <I18nProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" richColors />
            <Screens/>
          </BrowserRouter>
        </I18nProvider>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
